/* ==========================================================================
   Tier Card Component
   Task F33: Individual pricing tier card for pricing section
   ========================================================================== */

import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

export interface TierInfo {
  name: string
  monthlyPrice: number // dollars
  annualPrice: number // dollars (total per year)
  scriptLimit: number
  overageRate: string
  features: string[]
}

interface TierCardProps {
  tier: TierInfo
  billingCycle: 'monthly' | 'annual'
  isPopular: boolean
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents)
}

function formatScriptLimit(limit: number): string {
  return new Intl.NumberFormat('en-US').format(limit)
}

export function TierCard({ tier, billingCycle, isPopular }: TierCardProps) {
  const displayPrice =
    billingCycle === 'monthly'
      ? tier.monthlyPrice
      : Math.round(tier.annualPrice / 12)
  const period = '/mo'
  const billingNote =
    billingCycle === 'annual'
      ? `$${formatPrice(tier.annualPrice)} billed annually`
      : 'billed monthly'

  return (
    <Card
      className={`relative flex flex-col p-6 sm:p-8 ${
        isPopular
          ? 'border-2 border-deep-purple ring-1 ring-deep-purple/20'
          : 'border border-charcoal/10'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-deep-purple text-white text-xs font-bold px-3 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}

      {/* Tier name */}
      <h3 className="font-serif text-xl text-charcoal mb-2">{tier.name}</h3>

      {/* Price */}
      <div className="mb-1">
        <span className="font-serif text-4xl text-charcoal">
          ${formatPrice(displayPrice)}
        </span>
        <span className="text-charcoal/60 text-sm">{period}</span>
      </div>
      <p className="text-xs text-charcoal/50 mb-6">{billingNote}</p>

      {/* Script limit */}
      <div className="bg-soft-lavender/50 rounded-lg px-4 py-3 mb-6">
        <p className="text-sm font-semibold text-charcoal">
          {formatScriptLimit(tier.scriptLimit)} scripts/month
        </p>
        <p className="text-xs text-charcoal/60">
          {tier.overageRate} per additional script
        </p>
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-1">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Check
              className="h-4 w-4 text-deep-purple mt-0.5 flex-shrink-0"
              aria-hidden="true"
            />
            <span className="text-sm text-charcoal/80">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Link to="/contact-sales" className="mt-auto">
        <Button
          variant={isPopular ? 'primary' : 'secondary'}
          size="lg"
          className="w-full"
        >
          Contact Sales
        </Button>
      </Link>
    </Card>
  )
}

export default TierCard
